import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { CheckCircle2, Circle, Pencil, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { PRICING_TYPE_LABELS, type MenuItemSortDir, type MenuItemSortKey } from "@/types/menu";
import { getDietaryTagLabel, type DietaryTag } from "@/hooks/useDietaryTags";
import type { MenuItemRow } from "@/hooks/useMenuItems";

const SORT_OPTIONS: { label: string; key: MenuItemSortKey; dir: MenuItemSortDir }[] = [
  { label: "Name: A to Z", key: "name", dir: "asc" },
  { label: "Name: Z to A", key: "name", dir: "desc" },
  { label: "Category: A to Z", key: "category", dir: "asc" },
  { label: "Category: Z to A", key: "category", dir: "desc" },
  { label: "Price: low to high", key: "price", dir: "asc" },
  { label: "Price: high to low", key: "price", dir: "desc" },
];
const sortValue = (key: MenuItemSortKey, dir: MenuItemSortDir) => `${key}-${dir}`;

interface MenuItemMobileListProps {
  items: MenuItemRow[];
  selected: Set<string>;
  dietaryTags: DietaryTag[] | undefined;
  sortKey: MenuItemSortKey;
  sortDir: MenuItemSortDir;
  onSortChange: (key: MenuItemSortKey, dir: MenuItemSortDir) => void;
  onToggleSelect: (id: string) => void;
  onToggleAll: () => void;
  onToggleActive: (id: string, active: boolean) => void;
  onEdit: (item: MenuItemRow) => void;
  onDelete: (id: string) => void;
  className?: string;
}

/** Card list that replaces the Menu Items table below the md breakpoint (#693). */
export function MenuItemMobileList({
  items,
  selected,
  dietaryTags,
  sortKey,
  sortDir,
  onSortChange,
  onToggleSelect,
  onToggleAll,
  onToggleActive,
  onEdit,
  onDelete,
  className,
}: MenuItemMobileListProps) {
  const allSelected = items.length > 0 && selected.size === items.length;
  const handleSort = (value: string) => {
    const option = SORT_OPTIONS.find((o) => sortValue(o.key, o.dir) === value);
    if (option) onSortChange(option.key, option.dir);
  };

  return (
    <div data-testid="menu-item-mobile-list" className={cn("space-y-3", className)}>
      <div className="flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 py-2 pr-2 text-sm">
          <Checkbox checked={allSelected} onCheckedChange={onToggleAll} aria-label="Select all items" />
          Select all
        </label>
        <Select value={sortValue(sortKey, sortDir)} onValueChange={handleSort}>
          <SelectTrigger className="h-9 w-[170px]" aria-label="Sort items">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((o) => (
              <SelectItem key={sortValue(o.key, o.dir)} value={sortValue(o.key, o.dir)}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {items.map((item) => (
        <MenuItemCard
          key={item.id}
          item={item}
          isSelected={selected.has(item.id)}
          dietaryTags={dietaryTags}
          onToggleSelect={onToggleSelect}
          onToggleActive={onToggleActive}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

interface MenuItemCardProps {
  item: MenuItemRow;
  isSelected: boolean;
  dietaryTags: DietaryTag[] | undefined;
  onToggleSelect: (id: string) => void;
  onToggleActive: (id: string, active: boolean) => void;
  onEdit: (item: MenuItemRow) => void;
  onDelete: (id: string) => void;
}

function MenuItemCard({ item, isSelected, dietaryTags, onToggleSelect, onToggleActive, onEdit, onDelete }: MenuItemCardProps) {
  return (
    <div data-testid="menu-item-card" className={cn("rounded-lg border p-3 space-y-2", isSelected ? "bg-muted/30" : "bg-card")}>
      <div className="flex items-start gap-2">
        <label className="-m-2 flex shrink-0 items-center p-3">
          <Checkbox checked={isSelected} onCheckedChange={() => onToggleSelect(item.id)} aria-label={`Select ${item.name}`} />
        </label>
        <Avatar className="h-9 w-9 shrink-0">
          {item.image_url && <AvatarImage src={item.image_url} alt={item.name} />}
          <AvatarFallback className="text-xs">{item.name.charAt(0)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <button type="button" className="block w-full text-left text-sm font-medium [overflow-wrap:anywhere] hover:underline" onClick={() => onEdit(item)}>
            {item.name}
          </button>
          <p className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
            {item.category?.name ?? "—"}
            {item.sku ? ` · SKU: ${item.sku}` : ""}
          </p>
        </div>
        <span className="shrink-0 pt-0.5" title={item.square_catalog_id ? `Synced to Square: ${item.square_catalog_id}` : "Not synced to Square"}>
          {item.square_catalog_id ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <Circle className="h-4 w-4 text-muted-foreground/40" />}
          <span className="sr-only">{item.square_catalog_id ? "Synced to Square" : "Not synced to Square"}</span>
        </span>
      </div>
      {item.description && <p className="line-clamp-2 text-xs text-muted-foreground [overflow-wrap:anywhere]">{item.description}</p>}
      <MenuItemPrices item={item} />
      {item.dietary_tags && item.dietary_tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {item.dietary_tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs">{getDietaryTagLabel(dietaryTags, tag)}</Badge>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-2 border-t pt-1">
        <label className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
          <Switch checked={item.is_active} onCheckedChange={(checked) => onToggleActive(item.id, checked)} aria-label={`${item.name} active`} />
          {item.is_active ? "Active" : "Inactive"}
        </label>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" aria-label={`Edit ${item.name}`} onClick={() => onEdit(item)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={`Delete ${item.name}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete "{item.name}"?</AlertDialogTitle>
                <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => onDelete(item.id)}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}

/** Every price line for an item; shared with the desktop table's Pricing cell. */
export function MenuItemPrices({ item }: { item: MenuItemRow }) {
  return (
    <div className="flex flex-col gap-0.5">
      {item.prices?.map((p) => (
        <span key={p.id} className="text-sm">
          ${Number(p.price).toFixed(2)} {PRICING_TYPE_LABELS[p.pricing_type as keyof typeof PRICING_TYPE_LABELS] ?? p.pricing_type}
          {p.label ? ` (${p.label})` : ""}
          {p.serves ? ` · serves ${p.serves}` : ""}
        </span>
      ))}
      {!item.prices?.length && <span className="text-sm text-muted-foreground">No pricing</span>}
    </div>
  );
}
